import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AppInfoService } from '../services';
import type { AppInfo } from '../types';

let cachedAppInfo: AppInfo | null = null;
let isLoading = false;
let loadingPromise: Promise<void> | null = null;

export const useAppInfo = () => {
  const { t } = useTranslation();
  const [appInfo, setAppInfo] = useState<AppInfo | null>(cachedAppInfo);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAppInfo = async () => {
      // If already cached, use it
      if (cachedAppInfo) {
        setAppInfo(cachedAppInfo);
        return;
      }

      // If already loading, wait for it
      if (isLoading && loadingPromise) {
        await loadingPromise;
        setAppInfo(cachedAppInfo);
        return;
      }

      // Start new loading
      isLoading = true;
      setLoading(true);
      setError(null);

      loadingPromise = (async () => {
        try {
          const result = await AppInfoService.getAppInfo();

          if (result.isSuccess && result.result?.data) {
            cachedAppInfo = result.result.data;
            setAppInfo(cachedAppInfo);
          } else {
            setError(t('app_info.error'));
          }
        } catch {
          setError(t('app_info.error'));
        } finally {
          setLoading(false);
          isLoading = false;
          loadingPromise = null;
        }
      })();

      await loadingPromise;
    };

    fetchAppInfo();
  }, [t]);

  return {
    appInfo,
    loading,
    error,
  };
};
